import React, {memo} from 'react';
import {Handle, Position} from 'reactflow';

import {Box, List, ListItem, ListItemText, Stack, Typography} from "@mui/material";
import {
    IconAlphabetLatin,
    IconCapture,
    IconSquare,
} from "@tabler/icons-react";
import {
    getPalette,
    hexToRgba,
    iconChipSx,
    nodeHeaderSx,
    nodeKickerSx,
    nodeShellSx,
    nodeSurfaceSx,
    nodeTitleSx,
    sectionSx,
} from "./nodeTheme";


function BlockNode({id, data, selected}) {
    const {steps, name} = data;
    const hasSteps = steps?.length > 0;
    const palette = getPalette("block");

    return (
        <Box className={"node"} sx={{minWidth: "200px", ...nodeShellSx(palette, {selected})}}>
            {/* target dot – up top, level with the node icon */}
            <Handle type="target" position={Position.Left} id={id} style={{top: 16}}/>
            <Box sx={nodeSurfaceSx}>
                <Box sx={{...nodeHeaderSx(palette), ...sectionSx({first: true, last: !hasSteps})}}>
                    <Box sx={iconChipSx(palette)}>
                        <IconSquare size={12} stroke={1.8}/>
                    </Box>
                    <Box sx={{minWidth: 0, flex: 1}}>
                        <Typography component="span" sx={nodeKickerSx(palette)}>block</Typography>
                        <Typography noWrap sx={nodeTitleSx()}>{name}</Typography>
                    </Box>
                </Box>
                {hasSteps && (
                    <Box className="custom-node__body" sx={{background: "#FFFFFF", ...sectionSx({last: true})}}>
                        <List sx={{p: 0, m: 0}}>
                            {steps.map((step, index) => {
                                const isLast = index === steps.length - 1;
                                return {
                                    "text": <ListItem
                                        key={step?.id}
                                        sx={{
                                            position: "relative",
                                            borderBottom: isLast ? 0 : `1px solid ${palette.border}`,
                                            py: 0.6, px: 1, m: 0,
                                            ...(isLast && sectionSx({last: true})),
                                        }}
                                        className="custom-node__select"
                                    >
                                        <ListItemText sx={{p: 0, m: 0}}>
                                            <Stack direction={"row"} gap={0.75} alignItems={"center"} sx={{width: '100%'}}>
                                                <IconAlphabetLatin size={12} color={palette.accent}/>
                                                <Typography sx={{flex: 1, fontSize: 9, color: "#475569"}} align={"left"}>
                                                    {step?.text}
                                                </Typography>
                                            </Stack>
                                        </ListItemText>
                                        {isLast &&
                                            <Handle type="source" position={Position.Right} id={step?.id}/>}
                                    </ListItem>,
                                    "choice": <ListItem
                                        key={step?.id}
                                        sx={{
                                            position: "relative",
                                            bgcolor: hexToRgba(palette.accent, 0.06),
                                            borderBottom: isLast ? 0 : `1px solid ${palette.border}`,
                                            py: 0.6, px: 1, m: 0,
                                            ...(isLast && sectionSx({last: true})),
                                        }}
                                        className="custom-node__select"
                                    >
                                        <ListItemText sx={{p: 0, m: 0}}>
                                            <Stack direction={"row"} gap={0.75} alignItems={"center"} sx={{width: '100%'}}>
                                                <IconCapture size={12} color={palette.accent}/>
                                                <Typography noWrap sx={{flex: 1, fontSize: 9, color: "#1E293B"}}
                                                            align={"left"} fontWeight={600}>
                                                    {step?.text}
                                                </Typography>
                                            </Stack>
                                        </ListItemText>
                                    </ListItem>,
                                }[step.type]
                            })}
                        </List>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default memo(BlockNode);
